import { getApiBaseUrl } from '../config/api';

// FEAT-041 — Web client for the user-facing reporting endpoint.
// The endpoint is silent: the response only confirms the report was filed.

export type ReportTargetType = 'PROFILE' | 'MESSAGE';

export type ReportReasonCode =
  | 'SPAM'
  | 'HARASSMENT'
  | 'UNSAFE_CONTENT'
  | 'MISREPRESENTED_CREDENTIALS'
  | 'OTHER';

export interface ReportReasonOption {
  value: ReportReasonCode;
  label: string;
  helperText: string;
}

// Copy is intentionally neutral and elderly-friendly per §1.2 / §18.22:
// "Tell us what's wrong" rather than "report violation".
export const REPORT_REASON_OPTIONS: ReportReasonOption[] = [
  {
    value: 'SPAM',
    label: 'Spam or unwanted ads',
    helperText: 'Repeated or off-topic promotional messages.',
  },
  {
    value: 'HARASSMENT',
    label: 'Harassment or unkind behavior',
    helperText: 'Disrespectful, threatening, or hurtful language.',
  },
  {
    value: 'UNSAFE_CONTENT',
    label: 'Unsafe content',
    helperText: 'Content that feels unsafe or against community values.',
  },
  {
    value: 'MISREPRESENTED_CREDENTIALS',
    label: 'Credentials might be misrepresented',
    helperText: 'Claims about licenses or training that may not be accurate.',
  },
  {
    value: 'OTHER',
    label: 'Something else',
    helperText: "We'll review and follow up if needed.",
  },
];

export const REPORT_CONTEXT_MAX_CHARS = 500;

export interface CreateReportRequest {
  targetType: ReportTargetType;
  targetId: string;
  reasonCode: ReportReasonCode;
  contextText?: string;
}

export interface CreateReportResponse {
  report: {
    id: string;
    status: string;
    createdAt: string;
  };
}

class ReportService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async createReport(input: CreateReportRequest): Promise<CreateReportResponse> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/reports`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message = errorBody?.error?.message || 'Failed to file report';
      throw new Error(message);
    }

    return response.json();
  }
}

export const reportService = new ReportService();
